/**
 * Landing Page
 * CCH Care - Version 1.0
 * 
 * Public-facing page introducing CCH Care.
 * Provides Login and Get Started entry points.
 */

import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../config/constants';

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#0B1929', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif' }}>
      
      {/* Navigation */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 32px', maxWidth: 1200, margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: COLORS.primary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>🏥</div>
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>CCH Care</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '10px 20px', background: 'transparent', border: 'none',
              color: '#93C5FD', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >Login</button>
          <button
            onClick={() => navigate('/get-started')}
            style={{
              padding: '10px 24px', background: COLORS.primary, border: 'none',
              borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >Start Free Trial</button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{
        textAlign: 'center', padding: '80px 24px 60px', maxWidth: 800, margin: '0 auto',
      }}>
        <h1 style={{
          fontSize: 'clamp(32px, 5vw, 52px)', fontWeight: 800, lineHeight: 1.15,
          marginBottom: 20, letterSpacing: '-0.02em',
        }}>
          Streamlined ADL Documentation{' '}
          <span style={{ color: '#60A5FA', fontStyle: 'italic', fontWeight: 700 }}>
            for Long-Term Care
          </span>
        </h1>
        <p style={{
          fontSize: 'clamp(16px, 2.5vw, 19px)', color: '#94A3B8', lineHeight: 1.7,
          maxWidth: 600, margin: '0 auto 36px',
        }}>
          Tablet-first ADL flow sheets designed for CNAs at bedside. 
          HIPAA-compliant documentation with real-time compliance monitoring.
        </p>
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/get-started')}
            style={{
              padding: '16px 36px', background: COLORS.primary, border: 'none',
              borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 16px rgba(27, 107, 147, 0.4)',
            }}
          >Get Started</button>
          <button
            onClick={() => {
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              padding: '16px 36px', background: 'transparent',
              border: '2px solid rgba(255,255,255,0.2)',
              borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            }}
          >Learn More</button>
        </div>
      </section>

      {/* Feature Cards */}
      <section id="features" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 20, maxWidth: 1000, margin: '0 auto', padding: '40px 24px 80px',
      }}>
        {[
          { icon: '🛡️', title: 'HIPAA Compliant', desc: 'Full audit trails, digital signatures, and encrypted data at rest and in transit' },
          { icon: '📱', title: 'Tablet-First', desc: 'Touch-optimized interface designed for CNAs documenting at bedside with gloved hands' },
          { icon: '⏰', title: 'Real-Time Alerts', desc: 'Automatic notifications for missed repositioning, skin check failures, and low intake' },
          { icon: '📊', title: 'Compliance Reports', desc: 'Shift summaries, repositioning compliance, and nutrition tracking at your fingertips' },
        ].map((feature) => (
          <div key={feature.title} style={{
            padding: 28, borderRadius: 14,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 28, marginBottom: 14 }}>{feature.icon}</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#fff' }}>{feature.title}</div>
            <div style={{ fontSize: 14, color: '#94A3B8', lineHeight: 1.6 }}>{feature.desc}</div>
          </div>
        ))}
      </section>

      {/* Details Section */}
      <section style={{
        background: 'rgba(255,255,255,0.02)',
        borderTop: '1px solid rgba(255,255,255,0.06)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '80px 24px',
      }}>
        <div style={{
          maxWidth: 1000, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 48, alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 20, letterSpacing: '-0.01em' }}>
              Everything you need to document resident care
            </h2>
            <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
              CCH Care provides a complete ADL documentation platform built specifically 
              for long-term care facilities. Replace paper flow sheets with digital 
              workflows that improve accuracy and save time.
            </p>
            {[
              'Hygiene & personal care tracking',
              'Meal intake and fluid monitoring',
              'Q2H repositioning compliance',
              'Safety and environment checklists',
              'Digital sign-off with attestation',
              'Role-based access for CNAs and supervisors',
            ].map((item) => (
              <div key={item} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                marginBottom: 12, fontSize: 15,
              }}>
                <span style={{ color: '#60A5FA', fontSize: 16 }}>✓</span>
                <span style={{ color: '#CBD5E1' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA Card */}
          <div style={{
            padding: 36, borderRadius: 16,
            background: 'linear-gradient(135deg, rgba(27, 107, 147, 0.3), rgba(27, 107, 147, 0.15))',
            border: '1px solid rgba(27, 107, 147, 0.3)',
          }}>
            <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Start your free trial</h3>
            <p style={{ color: '#94A3B8', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              14 days free. No credit card required. Full access to all features.
            </p>
            <button
              onClick={() => navigate('/get-started')}
              style={{
                width: '100%', padding: 16, background: COLORS.primary,
                border: 'none', borderRadius: 10, color: '#fff',
                fontSize: 16, fontWeight: 700, cursor: 'pointer',
              }}
            >Get Started Free →</button>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{ textAlign: 'center', padding: '80px 24px' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>
          Ready to modernize your ADL documentation?
        </h2>
        <p style={{ color: '#94A3B8', fontSize: 15, marginBottom: 32 }}>
          Join healthcare organizations that trust CCH Care for their resident care documentation needs.
        </p>
        <button
          onClick={() => navigate('/get-started')}
          style={{
            padding: '16px 40px', background: COLORS.primary, border: 'none',
            borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', boxShadow: '0 4px 16px rgba(27, 107, 147, 0.4)',
          }}
        >Start Free Trial</button>
      </section>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '24px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        maxWidth: 1200, margin: '0 auto', flexWrap: 'wrap', gap: 16,
      }}>
        <div style={{ color: '#475569', fontSize: 13 }}>
          © 2026 CCH Care. All rights reserved.
        </div>
        <div style={{ display: 'flex', gap: 24 }}>
          {['Privacy Policy', 'Terms of Service', 'Contact'].map((link) => (
            <span key={link} style={{ color: '#64748B', fontSize: 13, cursor: 'pointer' }}>
              {link}
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
}
